import { Button, Form } from '@amzn/awsui-components-react-v2';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormSectionMode } from '../../../../common/constants/forms';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import { selectUser } from '../../../../common/store/slices/userSlice';
import useFormValidation from '../../../../common/utils/formValidation';
import { handleScrollToError } from '../../../../common/utils/scrollToError';
import {
    ActivityData,
    CloneActivityData,
    CreateActivityData,
    DeliverySession,
} from '../../../interfaces/activity';
import { MeridiemFieldValue } from '../../../interfaces/meridiem';
import {
    createActivity,
    selectIsLoading,
} from '../../../store/slices/selectedActivitySlice';
import {
    ActivityFormSectionProps,
    ActivitySessionControlArrayFormValues,
    createActivityValidationConfig,
    getSessionAttributeEditorItem,
    SessionAttributeEditorItem,
    sessionAttributeValidationConfig,
} from '../Common/Common';
import ActivityFormSection from '../FormSections/ActivityFormSection';
import { CLONE_CONFIG, cloneHelpers } from './activityCloneConfig';

const dayInMilliseconds = 86400000;

export const getUTCMilliseconds = (
    dateString: string,
    timeString: string,
    deliveryTimezone: string,
) => {
    // if emtpy, return invalid number, so session date is set empty
    if (dateString === '') return NaN;
    // get the utc time for the delivery location's time
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const utcTime = dayjs
        .tz(dateString + ' ' + timeString, deliveryTimezone)
        .utc();
    return utcTime.toDate().getTime();
};

export const convertTo24HourFormat = (
    time: string,
    meridiem: MeridiemFieldValue,
) => {
    if (!time) {
        return '';
    }

    let [hours, minutes] = time.split(':');

    if (hours === '12') {
        hours = '00';
    }

    if (meridiem === MeridiemFieldValue.Pm) {
        hours = `${parseInt(hours, 10) + 12}`;
    }

    return `${hours}:${minutes}`;
};

export const getDeliverySession = (
    sessionItem: SessionAttributeEditorItem,
    timeZone: string,
): DeliverySession => {
    const militaryStartTime = convertTo24HourFormat(
        sessionItem.startTime,
        sessionItem.startTimeMeridiemFieldOption,
    );
    const militaryEndTime = convertTo24HourFormat(
        sessionItem.endTime,
        sessionItem.endTimeMeridiemFieldOption,
    );

    const millisecondStartTime = getUTCMilliseconds(
        sessionItem.dateString,
        militaryStartTime,
        timeZone,
    );
    let millisecondEndTime = getUTCMilliseconds(
        sessionItem.dateString,
        militaryEndTime,
        timeZone,
    );

    // if the end time is earlier than the start time
    // it means we're out of the bounds of the previous 24 hours
    // we need to offset by a day
    if (millisecondStartTime > millisecondEndTime) {
        millisecondEndTime = millisecondEndTime + dayInMilliseconds;
    }

    return {
        start_timestamp: millisecondStartTime / 1000,
        end_timestamp: millisecondEndTime / 1000,
    };
};

const ActivityCloneForm = ({
    createActivityFormState,
}: {
    createActivityFormState: ActivityData;
}) => {
    const isLoading = useSelector(selectIsLoading);
    const [customers, setCustomers] = useState<Array<number>>([]);
    const [invoices, setInvoices] = useState<Array<number>>([]);
    const [autoAssignInstructor, setAutoAssignInstructor] = useState<
        boolean | undefined
    >(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const { addNotification } = useNotifications();

    const {
        isInvalid,
        isControlArrayInvalid,
        errors,
        controlArrayErrors,
        validateForm,
        validateFormControlArray,
    } = useFormValidation<
        Omit<CloneActivityData, 'delivery_sessions'>,
        ActivitySessionControlArrayFormValues
    >();

    const [formValues, setFormValues] = useState(createActivityFormState);

    const [partnerInitiative, setPartnerInitiative] = useState<string>('');

    const [courseDays, setCourseDays] = useState<number>();

    const handleCourseDaysChange = (courseDays: number) => {
        setCourseDays(courseDays);
    };

    const [sessionAttributeEditorItems, setSessionAttributeEditorItems] =
        useState(
            formValues.delivery_sessions.map((item) =>
                getSessionAttributeEditorItem(
                    item,
                    formValues.delivery_timezone,
                ),
            ) || [],
        );

    useEffect(() => {
        if (createActivityFormState && user) {
            setFormValues((values) => ({
                ...values,
                ...{
                    delivery_sessions: createActivityFormState
                        ? createActivityFormState.delivery_sessions
                        : [],
                    operations_owner:
                        user?.profile.full_name ??
                        createActivityFormState.operations_owner,
                    operations_owner_email:
                        user?.profile.email ??
                        createActivityFormState.operations_owner_email,
                    scheduler:
                        user?.profile.full_name ??
                        createActivityFormState.scheduler,
                    scheduler_email:
                        user?.profile.email ??
                        createActivityFormState.scheduler_email,
                    activity_status: 'Hold',
                },
            }));
        }
    }, [createActivityFormState, user]);

    const handleFieldEvent = useCallback(
        (changes: Partial<Omit<CreateActivityData, 'delivery_sessions'>>) => {
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    const validateAndHandleFieldEvent = useCallback(
        (changes: Partial<Omit<CreateActivityData, 'delivery_sessions'>>) => {
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    const handleSessionItemEvent = (
        sessionItems: Array<SessionAttributeEditorItem>,
    ) => {
        setSessionAttributeEditorItems(sessionItems);
    };

    const handleCreateActivity = async () => {
        const invalid = validateForm(
            formValues,
            createActivityValidationConfig,
        );
        const arrayInvalid = validateFormControlArray(
            { sessionAttributeEditorItems },
            sessionAttributeValidationConfig,
        );
        const isActivityFormInvalid = invalid || arrayInvalid;
        formValues.course_days = courseDays ? courseDays : 1;
        if (!isActivityFormInvalid) {
            // Build activity data using clone config
            const activityData: CloneActivityData = {
                ...cloneHelpers.buildBaseFields(formValues),
                ...CLONE_CONFIG.overrides,
                operations_owner: formValues.operations_owner,
                operations_owner_email: formValues.operations_owner_email,
                scheduler: formValues.scheduler,
                scheduler_email: formValues.scheduler_email,
                delivery_sessions: cloneHelpers.processDeliverySessions(
                    sessionAttributeEditorItems,
                    getDeliverySession,
                    formValues.delivery_timezone,
                ),
                billing_invoices: cloneHelpers.processInvoices(
                    formValues.billing_invoices ?? [],
                    invoices,
                ),
                customers: cloneHelpers.processCustomers(
                    formValues.customers ?? [],
                    customers,
                ),
                auto_assign_instructor: autoAssignInstructor,
            } as CloneActivityData;
            const { isSuccessful, createdActivityId } = await dispatch<any>(
                createActivity(activityData),
            );

            addNotification({
                id: `create-activity-${Date.now()}`,
                ...(isSuccessful
                    ? {
                          type: 'success',
                          content: 'Activity cloned.',
                      }
                    : {
                          type: 'error',
                          content:
                              'An error occurred while cloning the activity.',
                      }),
            });

            if (isSuccessful) {
                history.push({
                    pathname: `/activities/${createdActivityId}`,
                });
            }
        } else {
            handleScrollToError();
        }
    };

    const handleCustomersChange = (customerIndexes: Array<number>) => {
        setCustomers(customerIndexes);
    };

    const handleInvoicesChange = (invoiceIndexes: Array<number>) => {
        setInvoices(invoiceIndexes);
    };

    const handleAutoAssignInstructorChange = (
        autoAssignInstructor: boolean | undefined,
    ) => {
        setAutoAssignInstructor(autoAssignInstructor);
    };

    const handlePartnerInitiativeChange = (partnerInitiative: string) => {
        setPartnerInitiative(partnerInitiative);
    };

    const createActivityProps: ActivityFormSectionProps<
        Omit<ActivityData, 'delivery_sessions'>
    > & {
        handleCourseDaysChange: (courseDays: number) => void;
        handleAutoAssignInstructorChange: (
            autoAssignInstructor: boolean | undefined,
        ) => void;
        handlePartnerInitiativeChange: (partnerInitiative: string) => void;
    } = {
        formValues,
        errors,
        handleFieldEvent,
        validateAndHandleFieldEvent,
        handleSessionItemEvent,
        sessionAttributeEditorItems,
        // Preserve the cloned session start/end times when the admin changes a
        // session date; otherwise the first-session date change would reset
        // times to course defaults (AWS-EP-Engineering-328).
        preserveSessionTimesOnDateChange: true,
        mode: FormSectionMode.Create,
        controlArrayErrors,
        handleCourseDaysChange,
        handleCustomersChange,
        handleInvoicesChange,
        handleAutoAssignInstructorChange,
        handlePartnerInitiativeChange,
    };

    useEffect(() => {
        if (isInvalid) {
            validateForm(formValues, createActivityValidationConfig);
        }

        if (isControlArrayInvalid) {
            validateFormControlArray(
                { sessionAttributeEditorItems },
                sessionAttributeValidationConfig,
            );
        }
    }, [
        isInvalid,
        formValues,
        validateForm,
        validateFormControlArray,
        sessionAttributeEditorItems,
        isControlArrayInvalid,
    ]);

    return (
        <section data-testid="ActivityCreateForm">
            <Form
                header="Add activity"
                actions={
                    <div className="awsui-util-action-stripe awsui-util-mb-m">
                        <div className="awsui-util-action-stripe-group">
                            <Button
                                variant="link"
                                className="admin-activity-cancel"
                                data-testid="ActivityCreateFormCancel"
                                onClick={() => {
                                    history.push({
                                        pathname: `/activities/${formValues.pk}`,
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="admin-activity-save"
                                data-testid="ActivityCreateFormAdd"
                                loading={isLoading}
                                onClick={handleCreateActivity}
                            >
                                {`${isLoading ? 'Adding' : 'Add'} activity`}
                            </Button>
                        </div>
                    </div>
                }
            >
                <ActivityFormSection {...createActivityProps} />
            </Form>
        </section>
    );
};

export default ActivityCloneForm;
